import {
  DesktopDatePicker as MuiDatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { useCallback, useState, type JSX } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Icon } from '@doxyme/icons';
import { DateInputField, type DateInputFieldProps } from './dateInputField';
import { useDateInputStyles } from './dateInput.styles';
import dayjs from 'dayjs';
import { IconButton, type IconButtonProps } from '../../iconButton';
import { useIntl, useLocale } from '@doxyme/locale';
import { type TextFieldProps } from '@mui/material/TextField';
import { getPickersInputLocaleText } from '../../../../utils';

const DATE_FORMAT_MUI = 'MM/DD/YYYY';

/*
 We have to use Date | null here and not 'undefined' as MUI considers `undefined` values to be uncontrolled components.
 If this type changes please check you don't start getting MUI uncontrolled --> controlled component errors.
 */
export type DateInputValue = Date | null;

export type DateInputProps = {
  value: DateInputValue;
  onChange: (value: DateInputValue) => void;
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  id?: string;
};

export const DateInput = ({
  value,
  onChange,
  disabled,
  hasError,
  className,
  id,
}: DateInputProps): JSX.Element => {
  const [open, setOpen] = useState(false);

  const styles = useDateInputStyles();
  const { formatMessage } = useIntl();

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const onChangeCallback = useCallback((data: dayjs.Dayjs | null) => {
    onChange(data === null ? null : data.toDate());
  }, []);

  const { locale } = useLocale();

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={locale}
      localeText={getPickersInputLocaleText(locale)}
    >
      <MuiDatePicker
        className={className}
        views={['year', 'month', 'day']}
        disablePast
        showDaysOutsideCurrentMonth
        fixedWeekNumber={6}
        format={DATE_FORMAT_MUI}
        value={value ? dayjs(value) : null}
        onChange={onChangeCallback}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        slots={{
          openPickerButton: (props) => (
            <IconButton
              icon={<Icon.Calendar type='stroke' />}
              {...props}
              aria-label={formatMessage({
                id: 'input.dateInput.openPicker.label',
              })}
              type='subtle'
              size='medium'
              onClick={props.onClick as IconButtonProps['onClick']}
            />
          ),
          clearButton: (props) => (
            <IconButton
              icon={<Icon.Cross type='filled' size='small' />}
              {...props}
              type='subtle'
              size='medium'
              title={formatMessage({ id: 'dateInput.clearButton' })}
            />
          ),
          switchViewIcon: Icon.ChevronDown,
          rightArrowIcon: (props) => (
            <Icon.ChevronRight {...props} size='small' />
          ),
          leftArrowIcon: (props) => (
            <Icon.ChevronLeft {...props} size='small' />
          ),
          textField: DateInputField,
        }}
        sx={{
          ...styles.icons,
          ...styles.input,
        }}
        slotProps={{
          textField: {
            hasError,
            id,
            isClearIconShown: false,
          } as unknown as DateInputFieldProps as TextFieldProps,
          field: { clearable: true },
          calendarHeader: {
            sx: styles.calendarHeader,
          },
          popper: {
            sx: styles.popper,
          },
          layout: {
            sx: {
              ...styles.calendarDayLabelsRow,
              ...styles.calendarDayLabels,
              ...styles.calendarDayValuesRow,
              ...styles.calendarDayValues,
              ...styles.calendarYearValues,
              ...styles.calendarMonthValues,
            },
          },
        }}
        disabled={disabled}
      />
    </LocalizationProvider>
  );
};
